import { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography
} from "@mui/material";

const formatMinutes = (totalSeconds) => {
    const safeTotalSeconds = Math.max(0, Number(totalSeconds || 0));
    const hours = Math.floor(safeTotalSeconds / 3600);
    const minutes = Math.floor((safeTotalSeconds % 3600) / 60);
    const seconds = safeTotalSeconds % 60;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const AdminUserEditDialog = ({ open, user, saving, onClose, onSave }) => {
    const [role, setRole] = useState("basic");
    const [hoursToAdd, setHoursToAdd] = useState("0");
    const [minutesToAdd, setMinutesToAdd] = useState("0");

    useEffect(() => {
        if (!user) {
            setRole("basic");
            setHoursToAdd("0");
            setMinutesToAdd("0");
            return;
        }

        setRole(user.role || "basic");
        setHoursToAdd("0");
        setMinutesToAdd("0");
    }, [user]);

    const handleSave = () => {
        const normalizedHours = Number(hoursToAdd || 0);
        const normalizedMinutes = Number(minutesToAdd || 0);
        const totalMinutesToAdd = (Number.isFinite(normalizedHours) ? normalizedHours : 0) * 60
            + (Number.isFinite(normalizedMinutes) ? normalizedMinutes : 0);

        onSave({
            userId: user?.id,
            role,
            minutesToAdd: totalMinutesToAdd
        });
    };

    const normalizedHoursToAdd = Math.max(0, Number(hoursToAdd || 0));
    const normalizedMinutesToAdd = Math.max(0, Number(minutesToAdd || 0));
    const totalAdditionalSeconds = ((normalizedHoursToAdd * 60) + normalizedMinutesToAdd) * 60;
    const currentAvailableSeconds = Number(user?.transcription?.availableSeconds || 0);
    const updatedAvailableSeconds = currentAvailableSeconds + totalAdditionalSeconds;

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>Modifica utente</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ pt: 1 }}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {[user?.name, user?.lastname].filter(Boolean).join(" ") || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {user?.email || "-"}
                        </Typography>
                    </Box>

                    <FormControl fullWidth size="small">
                        <InputLabel id="admin-user-role-label">Ruolo</InputLabel>
                        <Select
                            labelId="admin-user-role-label"
                            value={role}
                            label="Ruolo"
                            onChange={(event) => setRole(event.target.value)}
                        >
                            <MenuItem value="basic">Basic</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                    </FormControl>

                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1.5}>
                            <Box sx={{ width: 120 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Ore da aggiungere"
                                    value={hoursToAdd}
                                    onChange={(event) => setHoursToAdd(event.target.value)}
                                    inputProps={{ min: 0, step: 1 }}
                                />
                            </Box>
                            <Box sx={{ width: 110 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Minuti da aggiungere"
                                    value={minutesToAdd}
                                    onChange={(event) => setMinutesToAdd(event.target.value)}
                                    inputProps={{ min: 0, step: 1 }}
                                />
                            </Box>
                        </Stack>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                justifyContent="space-between"
                            >
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Minuti disponibili
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {formatMinutes(currentAvailableSeconds)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Totale dopo modifica
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {formatMinutes(updatedAvailableSeconds)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} disabled={saving} variant="outlined">
                    Chiudi
                </Button>
                <Button onClick={handleSave} disabled={saving} variant="contained">
                    Salva
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdminUserEditDialog;
